import { useId, useMemo, useState } from "react";
import { Link } from "wouter";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { useShareGate } from "@/hooks/useShareGate";
import { buildMetaTags, metaProblems } from "./metaUtils";

const TOOL_INFO_CONTENT = {
  what: `Generates the HTML meta tags a webpage needs for search engines and social sharing — title, description, and Open Graph tags — from the content details you provide.`,
  how: `Enter your page title, description, and other details, and the generated meta tag HTML is ready to copy into your page's head section.`,
  tip: `Aim for a title under about 60 characters and a description under about 160. Google truncates by pixel width (roughly 580px for titles and 920px for descriptions on desktop), so the limits here are soft: you can type past them and the meters will warn you.`,
};

// Rough pixel-width estimate: canvas measureText with a sans-serif face at the size Google roughly uses
// (title about 20px, description about 14px). It is an approximation, not Google's exact rendering.
let canvasCtx: CanvasRenderingContext2D | null | undefined;
function measure(text: string, px: number): number {
  try {
    if (canvasCtx === undefined) canvasCtx = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
    if (!canvasCtx) return text.length * px * 0.5;
    canvasCtx.font = `${px}px Arial, Helvetica, sans-serif`;
    return canvasCtx.measureText(text).width;
  } catch { return text.length * px * 0.5; }
}

function Meter({ label, width, limit }: { label: string; width: number; limit: number }) {
  const w = Math.round(width);
  const over = w > limit;
  return (
    <div className="mt-1" data-testid={`meter-${label}`}>
      <div className="h-1.5 w-full rounded bg-muted overflow-hidden" role="presentation">
        <div className={`h-full ${over ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${Math.min(100, (w / limit) * 100)}%` }} />
      </div>
      <p className={`text-xs mt-1 ${over ? "text-amber-600" : "text-muted-foreground"}`}>
        About {w}px of {limit}px (estimate){over ? ". Google will probably cut this off in search results." : "."}
      </p>
    </div>
  );
}

export default function MetaTagGen() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [author, setAuthor] = useState("");
  const [url, setUrl] = useState("");
  const [image, setImage] = useState("");
  const [siteName, setSiteName] = useState("");
  const [locale, setLocale] = useState("");
  const [twitterSite, setTwitterSite] = useState("");
  const [themeColor, setThemeColor] = useState("");
  const [noindex, setNoindex] = useState(false);
  const [nofollow, setNofollow] = useState(false);
  const uid = useId();
  const problems = metaProblems({ url, image, twitterSite, themeColor });
  const code = buildMetaTags({ title, description, keywords, author, url, image, siteName, locale, twitterSite, themeColor, noindex, nofollow, lang: "en" });
  const titleW = useMemo(() => measure(title, 20), [title]);
  const descW = useMemo(() => measure(description, 14), [description]);

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });
  const { gateShare } = useShareGate();

  const field = (key: string, label: React.ReactNode, input: (p: { id: string; "aria-invalid"?: boolean; "aria-describedby"?: string; className?: string }) => React.ReactNode, error?: string, hint?: React.ReactNode) => {
    const id = `${uid}-${key}`;
    return (
      <div>
        <Label htmlFor={id}>{label}</Label>
        {input({ id, "aria-invalid": error ? true : undefined, "aria-describedby": error ? `${id}-err` : undefined, className: error ? "border-destructive" : undefined })}
        {error && <p id={`${id}-err`} role="alert" className="text-xs text-destructive mt-1">{error}</p>}
        {hint}
      </div>
    );
  };
  const soft = (n: number, max: number) => (n > max ? <span className="text-amber-600"> ({n}/{max}, long)</span> : <span> ({n}/{max})</span>);

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          {field("title", <>Page Title{soft(title.length, 60)}</>, (p) => <Input {...p} value={title} onChange={e => setTitle(e.target.value)} />, undefined, <Meter label="title" width={titleW} limit={580} />)}
          {field("desc", <>Description{soft(description.length, 160)}</>, (p) => <Textarea {...p} value={description} onChange={e => setDescription(e.target.value)} className="h-20" />, undefined, <Meter label="description" width={descW} limit={920} />)}
          {field("kw", "Keywords (comma-separated, obsolete: ignored by Google)", (p) => <Input {...p} value={keywords} onChange={e => setKeywords(e.target.value)} />, undefined, <p className="text-xs text-muted-foreground mt-1">Google has ignored the keywords tag for years. Leave it empty unless another system you use still reads it.</p>)}
          {field("author", "Author", (p) => <Input {...p} value={author} onChange={e => setAuthor(e.target.value)} />)}
          {field("url", "Page URL (canonical)", (p) => <Input {...p} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/page" />, problems.url)}
          {field("image", "Share image URL (optional, 1200×630 works best)", (p) => <Input {...p} value={image} onChange={e => setImage(e.target.value)} placeholder="https://example.com/image.png" />, problems.image)}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field("site", "Site name (og:site_name)", (p) => <Input {...p} value={siteName} onChange={e => setSiteName(e.target.value)} />)}
            {field("locale", "Locale (og:locale)", (p) => <Input {...p} value={locale} onChange={e => setLocale(e.target.value)} placeholder="en_US" />)}
            {field("tw", "X / Twitter handle (twitter:site)", (p) => <Input {...p} value={twitterSite} onChange={e => setTwitterSite(e.target.value)} placeholder="@yourbrand" />, problems.twitterSite)}
            {field("theme", "Theme color", (p) => <Input {...p} value={themeColor} onChange={e => setThemeColor(e.target.value)} placeholder="#1a73e8" />, problems.themeColor)}
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={noindex} onChange={e => setNoindex(e.target.checked)} /> Don't index this page (noindex)</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={nofollow} onChange={e => setNofollow(e.target.checked)} /> Don't follow links on this page (nofollow)</label>
          </div>
        </div>
        <div>
          <Label>Generated Meta Tags</Label>
          <pre className="mt-2 p-4 rounded-lg border bg-card text-xs font-mono whitespace-pre-wrap overflow-auto max-h-80">{blocked ? "— blocked at today's usage limit —" : code}</pre>
          <Button size="sm" className="mt-2" disabled={blocked} onClick={() => void gateShare(async () => { try { await navigator.clipboard.writeText(code); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } })}>Copy HTML</Button>
          <p className="text-xs text-muted-foreground mt-3">Want to see how it looks when shared? See how it will look when shared in the <Link href="/tool/og-preview" className="underline">OG / Twitter Card Preview</Link>.</p>
        </div>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
